import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { KpiCard } from '../components/KpiCard'
import { CostChart } from '../components/CostChart'
import { AlertPanel } from '../components/AlertPanel'
import { useAdminData } from '../hooks/useAdminData'
import { api } from '../../api/client'

interface OverviewData {
  mrr_usd: number
  cost_month_usd: number
  gross_margin: number
  avg_cost_per_user: number
  active_subscribers: number
  trialing: number
  failed_payments: number
  pending_users: number
  plan_distribution: Record<string, number>
  daily_costs: Array<{
    date: string
    total: number
    anthropic: number
    voyage: number
    active_users: number
    cost_per_user: number
  }>
  unresolved_alerts: Array<{
    id: string
    alert_type: string
    severity: string
    title: string
    description?: string
    created_at: string
  }>
}

interface MemoryCalibration {
  since_days: number
  total_saves: number
  distribution: Array<{ mode: string; count: number; pct: number }>
  target_distribution: Record<string, number>
  revert_rate: Array<{ mode: string; saved: number; reverted: number; rate: number }>
  top_revert_reasons: Array<{ reason: string; count: number }>
}

export function OverviewPage() {
  const navigate = useNavigate()
  const { data, loading, refetch } = useAdminData<OverviewData>('/overview')
  const { data: calibration } = useAdminData<MemoryCalibration>('/memory-calibration')
  const [collecting, setCollecting] = useState(false)
  const [collectMsg, setCollectMsg] = useState<string | null>(null)

  const handleResolve = async (id: string) => {
    await api.patch(`/admin/alerts/${id}/resolve`, {})
    refetch()
  }

  const handleCollect = async () => {
    setCollecting(true)
    setCollectMsg(null)
    try {
      type DayResult = {
        date: string
        total_cost_usd: number
        active_users: number
        error?: string
      }
      const result = await api.post<{ yesterday: DayResult; today: DayResult }>(
        '/admin/collect',
      )
      const fmt = (d: DayResult) =>
        d.error ? `erro (${d.error.slice(0, 40)})` : `$${d.total_cost_usd.toFixed(2)}`
      setCollectMsg(`Ontem: ${fmt(result.yesterday)} · Hoje: ${fmt(result.today)}`)
      refetch()
    } catch (e: any) {
      setCollectMsg(`Erro: ${e.message || 'falhou'}`)
    } finally {
      setCollecting(false)
      setTimeout(() => setCollectMsg(null), 8000)
    }
  }

  const handleSyncPrompts = async () => {
    if (
      !confirm(
        'Actualizar prompts das rotinas de todos os utilizadores para as missões canónicas mais recentes? Não afecta schedule, activação, ou configuração — só nome/descrição/prompt.',
      )
    )
      return
    setCollecting(true)
    setCollectMsg(null)
    try {
      const result = await api.post<{
        users_scanned: number
        agents_updated: number
        agents_unchanged: number
      }>('/admin/sync-agent-prompts')
      setCollectMsg(
        `Sync OK — ${result.agents_updated} actualizados, ${result.agents_unchanged} já em dia (${result.users_scanned} users)`,
      )
    } catch (e: any) {
      setCollectMsg(`Erro no sync: ${e.message || 'falhou'}`)
    } finally {
      setCollecting(false)
      setTimeout(() => setCollectMsg(null), 10000)
    }
  }

  if (loading || !data) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--ink-muted)', fontFamily: 'var(--sans)' }}>
        {loading ? 'A carregar...' : 'Sem dados'}
      </div>
    )
  }

  const marginPct = (data.gross_margin * 100).toFixed(1)
  const isLowMargin = data.gross_margin < 0.65

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Page title + collect button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1
          style={{
            fontFamily: 'var(--serif)',
            fontSize: '28px',
            fontWeight: 600,
            color: 'var(--ink)',
            margin: 0,
          }}
        >
          Overview
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {collectMsg && (
            <span
              style={{
                fontFamily: 'var(--sans)',
                fontSize: '12px',
                color: collectMsg.startsWith('Erro') ? '#dc2626' : 'var(--accent)',
              }}
            >
              {collectMsg}
            </span>
          )}
          <button
            onClick={handleSyncPrompts}
            disabled={collecting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'transparent',
              color: 'var(--ink-light)',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: collecting ? 'wait' : 'pointer',
              opacity: collecting ? 0.6 : 1,
            }}
            title="Aplica as missões canónicas mais recentes a todas as rotinas default"
          >
            Sync prompts rotinas
          </button>
          <button
            onClick={handleCollect}
            disabled={collecting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid var(--border)',
              background: 'var(--ink)',
              color: '#fff',
              fontFamily: 'var(--sans)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: collecting ? 'wait' : 'pointer',
              opacity: collecting ? 0.6 : 1,
            }}
          >
            <RefreshCw
              size={14}
              style={{
                animation: collecting ? 'spin 1s linear infinite' : undefined,
              }}
            />
            {collecting ? 'A recolher...' : 'Recolher agora'}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* KPI row */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <KpiCard
          label="MRR"
          value={`$${data.mrr_usd.toFixed(0)}`}
          subtitle={`${data.active_subscribers} subscribers + ${data.trialing} trials`}
          accent
        />
        <KpiCard
          label="Custo Mensal"
          value={`$${data.cost_month_usd.toFixed(0)}`}
          subtitle={`$${data.avg_cost_per_user.toFixed(2)}/user/dia`}
        />
        <KpiCard
          label="Margem Bruta"
          value={`${marginPct}%`}
          warning={isLowMargin}
          subtitle={isLowMargin ? 'Abaixo do threshold (65%)' : undefined}
        />
        <KpiCard
          label="Pagamentos Falhados"
          value={`${data.failed_payments}`}
          warning={data.failed_payments > 0}
        />
        {/* Pending users — clickable; warning colour when > 0 so admins notice
            the moderation queue without having to open Utilizadores tab. */}
        <div
          onClick={() => navigate('/admin/utilizadores?status=pending')}
          style={{
            flex: 1,
            minWidth: '180px',
            cursor: 'pointer',
          }}
          title={
            data.pending_users > 0
              ? 'Clica para rever e aprovar'
              : 'Sem pedidos pendentes'
          }
        >
          <KpiCard
            label="Pendentes"
            value={`${data.pending_users}`}
            warning={data.pending_users > 0}
            subtitle={
              data.pending_users > 0 ? 'À espera de aprovação' : undefined
            }
          />
        </div>
      </div>

      {/* Chart + Alerts side-by-side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '24px', alignItems: 'start' }}>
        <CostChart data={data.daily_costs} />

        <div
          style={{
            background: 'rgba(255,255,255,0.8)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '20px',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--serif)',
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--ink)',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>Alertas</span>
            {data.unresolved_alerts.length > 0 && (
              <span
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: '11px',
                  fontFamily: 'var(--sans)',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '9999px',
                }}
              >
                {data.unresolved_alerts.length}
              </span>
            )}
          </div>
          <AlertPanel alerts={data.unresolved_alerts} onResolve={handleResolve} />
        </div>
      </div>

      {/* Plan distribution */}
      {Object.keys(data.plan_distribution).length > 0 && (
        <div
          style={{
            background: 'rgba(255,255,255,0.8)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '20px',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--serif)',
              fontSize: '18px',
              fontWeight: 600,
              color: 'var(--ink)',
              marginBottom: '16px',
            }}
          >
            Distribuicao por Plano
          </div>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
            {Object.entries(data.plan_distribution).map(([plan, count]) => (
              <div key={plan} style={{ textAlign: 'center' }}>
                <div
                  style={{
                    fontFamily: 'var(--serif)',
                    fontSize: '24px',
                    fontWeight: 600,
                    color: 'var(--ink)',
                  }}
                >
                  {count}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--sans)',
                    fontSize: '12px',
                    color: 'var(--ink-muted)',
                    textTransform: 'capitalize',
                  }}
                >
                  {plan}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calibração do chat — distribuição saved_mode + revert rate.
          Reveals whether the L1 60/30/10 target is holding in practice.
          Omitted when there are no chat saves yet in the window. */}
      {calibration && calibration.total_saves > 0 && (
        <div
          style={{
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--serif)',
                fontSize: '18px',
                fontWeight: 600,
                color: 'var(--ink)',
              }}
            >
              Calibração do chat
            </div>
            <div
              style={{
                fontFamily: 'var(--sans)',
                fontSize: '12px',
                color: 'var(--ink-muted)',
              }}
            >
              últimos {calibration.since_days}d · {calibration.total_saves} saves
            </div>
          </div>

          {/* Distribution — silent/disclosed/asked vs target */}
          <div style={{ marginBottom: '20px' }}>
            <div
              style={{
                fontFamily: 'var(--sans)',
                fontSize: '13px',
                color: 'var(--ink-muted)',
                marginBottom: '8px',
              }}
            >
              Distribuição vs alvo (60/30/10)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {['silent', 'disclosed', 'asked', 'unknown'].map((mode) => {
                const row = calibration.distribution.find((d) => d.mode === mode)
                const actual = row?.pct ?? 0
                const target = calibration.target_distribution[mode] ?? 0
                const delta = actual - target
                const offTarget = mode !== 'unknown' && Math.abs(delta) > 0.15
                return (
                  <div
                    key={mode}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '90px 1fr 110px',
                      alignItems: 'center',
                      gap: '10px',
                      fontFamily: 'var(--sans)',
                      fontSize: '13px',
                      color: 'var(--ink)',
                    }}
                  >
                    <div style={{ color: 'var(--ink-muted)' }}>{mode}</div>
                    <div style={{ position: 'relative', height: '14px', background: 'var(--cream)', borderRadius: '4px', overflow: 'hidden' }}>
                      {target > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            left: `${target * 100}%`,
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            background: 'var(--ink-muted)',
                            opacity: 0.4,
                          }}
                          title={`alvo ${Math.round(target * 100)}%`}
                        />
                      )}
                      <div
                        style={{
                          height: '100%',
                          width: `${actual * 100}%`,
                          background: offTarget ? '#dc2626' : 'var(--accent)',
                          opacity: 0.8,
                          transition: 'width 300ms',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        color: offTarget ? '#dc2626' : 'var(--ink-light)',
                      }}
                    >
                      {Math.round(actual * 100)}%
                      {target > 0 && mode !== 'unknown' && (
                        <span style={{ color: 'var(--ink-muted)', marginLeft: '6px' }}>
                          ({delta >= 0 ? '+' : ''}{Math.round(delta * 100)})
                        </span>
                      )}
                      <span style={{ color: 'var(--ink-muted)', marginLeft: '6px' }}>
                        {row?.count ?? 0}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Revert rate — overall + per mode */}
          <div style={{ marginBottom: '12px' }}>
            <div
              style={{
                fontFamily: 'var(--sans)',
                fontSize: '13px',
                color: 'var(--ink-muted)',
                marginBottom: '8px',
              }}
            >
              Revert rate
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {calibration.revert_rate.map((r) => {
                const highRevert = r.mode !== 'all' && r.rate > 0.2 && r.saved >= 5
                return (
                  <div
                    key={r.mode}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: '80px',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'var(--serif)',
                        fontSize: '20px',
                        fontWeight: 600,
                        color: highRevert ? '#dc2626' : 'var(--ink)',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {Math.round(r.rate * 100)}%
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--sans)',
                        fontSize: '11px',
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {r.mode} · {r.reverted}/{r.saved}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Top revert reasons — only if any reverts with reasons */}
          {calibration.top_revert_reasons.length > 0 && (
            <div>
              <div
                style={{
                  fontFamily: 'var(--sans)',
                  fontSize: '13px',
                  color: 'var(--ink-muted)',
                  marginBottom: '8px',
                }}
              >
                Top razões de revert
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {calibration.top_revert_reasons.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontFamily: 'var(--sans)',
                      fontSize: '13px',
                      color: 'var(--ink-light)',
                    }}
                  >
                    <span>{r.reason}</span>
                    <span style={{ color: 'var(--ink-muted)', fontVariantNumeric: 'tabular-nums' }}>
                      {r.count}x
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
